/**
 * Register-on-login + 7-day re-registration cadence.
 *
 * The shared lifecycle both products run from their authenticated tree:
 * register when an authenticated user appears (password login, SSO, AND
 * page reloads of an authenticated session), re-register every 7 days, and
 * re-register when the user id changes. `enabled` carries any extra
 * app-side gate (e.g. SMS kiosk mode — communal devices must never bind a
 * per-user token); flipping it tears the interval down.
 */
import { useEffect, useRef } from 'react';
import { REREGISTER_INTERVAL_MS } from '../constants';

export interface UseWebPushRegistrationScheduleOptions {
  /**
   * Current authenticated user id; `null`/`undefined` while signed out.
   * A change of id re-registers (fresh identity, fresh token binding).
   */
  userId: string | number | null | undefined;
  /** Extra app-side gate (e.g. `!isKioskMode`). Defaults to `true`. */
  enabled?: boolean;
  /**
   * Fire-and-forget registration callback. The latest render's closure is
   * used at every tick — no need to memoize.
   */
  register: () => unknown;
}

export function useWebPushRegistrationSchedule(
  options: UseWebPushRegistrationScheduleOptions,
): void {
  const { userId, enabled = true, register } = options;

  // Stable refs so the long-lived interval sees current values without
  // re-subscribing on every render.
  const registerRef = useRef(register);
  useEffect(() => {
    registerRef.current = register;
  }, [register]);
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    if (userId == null || !enabled) return;
    void registerRef.current();
    const interval = setInterval(() => {
      // Live guard: don't re-register if the user signed out mid-interval.
      if (userIdRef.current != null) void registerRef.current();
    }, REREGISTER_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userId, enabled]);
}
